import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Smile, Paperclip, Loader2 } from "lucide-react";
import EmojiPicker, { EmojiStyle, Theme } from "emoji-picker-react";
import { toast } from "sonner";

/**
 * Toolbar with prominent Emoji + Image buttons (matches Templates button styling).
 * - onEmoji(emoji: string): called with raw emoji char to insert into text.
 * - onAttach(media: { id, url, content_type, size }): called after a successful upload.
 * - compact: render as icon-only buttons (default false = with label).
 */
export default function AttachToolbar({ onEmoji, onAttach, testIdPrefix = "attach", compact = false }) {
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const fileRef = useRef(null);

  const openFile = () => {
    if (uploading) return;
    fileRef.current && fileRef.current.click();
  };

  const doUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        onAttach && onAttach(data);
      }
      toast.success(files.length === 1 ? "Image attached" : `${files.length} images attached`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid={`${testIdPrefix}-emoji-button`}
          >
            <Smile className="w-3.5 h-3.5 mr-1.5 text-[#D9232E]" />
            {compact ? "" : "Emoji"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="p-0 border-0 shadow-lg" data-testid={`${testIdPrefix}-emoji-picker`}>
          <EmojiPicker
            onEmojiClick={(e) => { onEmoji && onEmoji(e.emoji); setEmojiOpen(false); }}
            emojiStyle={EmojiStyle.NATIVE}
            theme={Theme.LIGHT}
            width={320}
            height={380}
            searchPlaceholder="Search emoji..."
            previewConfig={{ showPreview: false }}
            skinTonesDisabled
          />
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={openFile}
        disabled={uploading}
        data-testid={`${testIdPrefix}-image-button`}
      >
        {uploading ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <Paperclip className="w-3.5 h-3.5 mr-1.5 text-[#D9232E]" />
        )}
        {compact ? "" : "Image"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        onChange={doUpload}
        className="hidden"
        data-testid={`${testIdPrefix}-file-input`}
      />
    </div>
  );
}

/**
 * Preview strip for attached media — small thumbnails with a remove (x) button.
 */
export function AttachmentPreview({ items, onRemove, testIdPrefix = "attach" }) {
  if (!items || !items.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mb-2" data-testid={`${testIdPrefix}-previews`}>
      {items.map((m) => (
        <div key={m.id} className="relative group">
          <img
            src={m.url}
            alt="attachment"
            className="w-16 h-16 object-cover rounded-md border border-slate-200"
          />
          <button
            type="button"
            onClick={() => onRemove(m.id)}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] flex items-center justify-center opacity-90 hover:bg-[#D9232E]"
            title="Remove"
            data-testid={`${testIdPrefix}-remove-${m.id}`}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
